import { MOODIST_SOUNDS } from './moodist-catalog.generated'
import { MOODIST_LABELS } from './moodist-labels'

export type SoundscapeId = string
export type SoundscapeCategoryId = typeof MOODIST_SOUNDS[number]['category']
export type SoundscapeTone = 'blue' | 'cyan' | 'lilac' | 'green' | 'amber'

export interface SoundscapeDefinition {
  id: SoundscapeId
  name: string
  description: string
  family: string
  category: SoundscapeCategoryId
  tone: SoundscapeTone
  groupId: string
  groupName: string
  variantId: string
  audioUrl: string
  quality: '320 kbps' | '128 kbps' | '原始高保真'
}

export interface SoundscapeGroupDefinition {
  id: string
  name: string
  family: string
  category: SoundscapeCategoryId
  tone: SoundscapeTone
  variants: SoundscapeDefinition[]
}

export interface SoundscapeTrackState {
  id: SoundscapeId
  volume: number
}

export interface SoundscapeMix {
  id: string
  name: string
  tracks: SoundscapeTrackState[]
  builtIn?: boolean
  createdAt?: number
}

export const SOUNDSCAPE_PROVIDER = {
  name: 'Moodist',
  canvasUrl: 'https://moodist.mvze.net/',
  widgetsUrl: 'https://github.com/remvze/moodist',
  catalogue: { variantCount: MOODIST_SOUNDS.length },
} as const

export const MOODIST_AUDIO_ROOT = 'https://shangancard.me/media/moodist'

export const SOUNDSCAPE_CATEGORY_ORDER: SoundscapeCategoryId[] = [
  'nature', 'rain', 'animals', 'urban', 'places', 'transport', 'things',
  'noise',
]

export const SOUNDSCAPE_CATEGORY_LABELS: Record<SoundscapeCategoryId, string> = {
  nature: '自然', rain: '雨声', animals: '动物', urban: '城市', places: '场所', transport: '交通', things: '物件',
  noise: '噪音',
}

const CATEGORY_TONES: Record<SoundscapeCategoryId, SoundscapeTone> = {
  nature: 'blue', rain: 'cyan', animals: 'green', urban: 'amber', places: 'amber', transport: 'lilac', things: 'lilac',
  noise: 'green',
}

export function makeSoundscapeId(groupId: string, variantId: string): SoundscapeId {
  return `${groupId}::${variantId}`
}

export const SOUNDSCAPE_GROUPS: SoundscapeGroupDefinition[] = MOODIST_SOUNDS.map(group => {
  const index = MOODIST_SOUNDS.filter(sound => sound.category === group.category).findIndex(sound => sound.id === group.id)
  const name = MOODIST_LABELS[group.category]?.[index] || group.name
  const family = SOUNDSCAPE_CATEGORY_LABELS[group.category]
  const tone = CATEGORY_TONES[group.category]
  return {
    id: group.id,
    name,
    family,
    category: group.category,
    tone,
    variants: [{
      id: `moodist:${group.id}`,
      name,
      description: `${name} · ${group.name} · Moodist`,
      family,
      category: group.category,
      tone,
      groupId: group.id,
      groupName: name,
      variantId: group.id,
      audioUrl: `${MOODIST_AUDIO_ROOT}${group.path}`,
      quality: '原始高保真',
    }],
  }
})

export const SOUNDSCAPE_CATALOG: SoundscapeDefinition[] = SOUNDSCAPE_GROUPS.flatMap(group => group.variants)
export const SOUNDSCAPE_BY_ID = new Map(SOUNDSCAPE_CATALOG.map(item => [item.id, item]))
export const SOUNDSCAPE_GROUP_BY_ID = new Map(SOUNDSCAPE_GROUPS.map(group => [group.id, group]))

const LEGACY_ID_ALIASES: Record<string, SoundscapeId> = {
  ocean: makeSoundscapeId('waves', 'Beach'),
  wind: makeSoundscapeId('wind', 'Light'),
  brown: makeSoundscapeId('brown_noise', 'Brown Noise'),
  rain: makeSoundscapeId('rain', 'Rain'),
  forest: makeSoundscapeId('forest', 'Forest 1'),
  cafe: makeSoundscapeId('cafe', 'Cafe 1'),
  fire: makeSoundscapeId('fire', 'Bonfire 1'),
  stream: makeSoundscapeId('stream', 'Stream'),
  library: makeSoundscapeId('clock', 'Wall Clock'),
  white: makeSoundscapeId('white_noise', 'White noise'),
}

export function resolveSoundscapeId(id: string): SoundscapeId | null {
  const old = LEGACY_ID_ALIASES[id] || id
  const group = old.split('::')[0]
  const aliases: Record<string, string> = { rain: 'light-rain', forest: 'jungle', stream: 'river', fire: 'campfire', brown_noise: 'brown-noise', white_noise: 'white-noise', pink_noise: 'pink-noise', ocean: 'waves' }
  const resolved = id.startsWith('moodist:') ? id : `moodist:${aliases[group] || group}`
  return SOUNDSCAPE_BY_ID.has(resolved) ? resolved : null
}

const soundId = (group: string, variant: string) => resolveSoundscapeId(makeSoundscapeId(group, variant)) || 'moodist:wind'

export const BUILT_IN_MIXES: SoundscapeMix[] = [
  {
    id: 'deep-study',
    name: '深海自习',
    builtIn: true,
    tracks: [
      { id: soundId('waves', 'Beach'), volume: 0.72 },
      { id: soundId('wind', 'Light'), volume: 0.42 },
      { id: soundId('brown_noise', 'Brown Noise'), volume: 0.48 },
    ],
  },
  {
    id: 'forest-night',
    name: '林间夜读',
    builtIn: true,
    tracks: [
      { id: soundId('forest', 'Forest 1'), volume: 0.66 },
      { id: soundId('stream', 'Stream'), volume: 0.46 },
      { id: soundId('wind', 'Light'), volume: 0.32 },
    ],
  },
  {
    id: 'rain-reading',
    name: '雨窗阅读',
    builtIn: true,
    tracks: [
      { id: soundId('rain', 'rain on the window'), volume: 0.68 },
      { id: soundId('cafe', 'Cafe 1'), volume: 0.3 },
      { id: soundId('brown_noise', 'Brown Noise'), volume: 0.34 },
    ],
  },
  {
    id: 'quiet-library',
    name: '静谧书房',
    builtIn: true,
    tracks: [
      { id: soundId('clock', 'Wall Clock'), volume: 0.62 },
      { id: soundId('fire', 'Fireplace'), volume: 0.28 },
      { id: soundId('brown_noise', 'Brown Noise'), volume: 0.3 },
    ],
  },
]

export const DEFAULT_MIX = BUILT_IN_MIXES[0]

export function normalizeTracks(tracks: SoundscapeTrackState[]): SoundscapeTrackState[] {
  const unique = new Map<SoundscapeId, number>()
  for (const track of tracks) {
    const id = resolveSoundscapeId(track.id)
    if (!id || unique.has(id) || unique.size >= 3) continue
    unique.set(id, Math.min(1, Math.max(0, Number.isFinite(track.volume) ? track.volume : 0.5)))
  }
  return [...unique].map(([id, volume]) => ({ id, volume }))
}

export function mixSignature(tracks: SoundscapeTrackState[]) {
  return normalizeTracks(tracks)
    .map(track => `${track.id}:${track.volume.toFixed(2)}`)
    .sort()
    .join('|')
}
